import cloneDeep from 'lodash/cloneDeep';
import type { ActorRef, ActorRefFrom, Snapshot } from 'xstate';
import { assign, sendTo, setup } from 'xstate';

import type { QuestionAnswerModel } from '@webapp/common/resources/entities';
import { initialQuestionAnswer } from '@webapp/common/resources/entities';
import type { QuestionType, TestCheckStatus } from '@webapp/common/resources/survey';

import type { SavedQuestionAnswer } from '@webapp/survey/src/machine/lib';

import type { QuestionResponseEntity } from './question-response';
import type { QuestionResponseActor, ResponseEventParams } from './response-machine';
import { QuestionResponseMachine } from './response-machine';

export interface QuestionAnswerEntity extends QuestionAnswerModel {
    response?: QuestionResponseEntity;
}

type ChildEvents =
    | ({
          type: 'QUESTION_RESPONSE_CHANGE';
      } & ResponseEventParams)
    | ({
          type: 'QUESTION_RESPONSE_CHANGE_COMMENT';
      } & ResponseEventParams)
    | ({
          type: 'QUESTION_RESPONSE_CHANGE_EXTRA';
      } & ResponseEventParams);

type ParentActor = ActorRef<Snapshot<unknown>, ChildEvents>;

export interface QuestionAnswerContext extends QuestionAnswerModel {
    questionId: number;
    forwardParent: number | null;
    questionType: QuestionType;
    response?: QuestionResponseActor;
    parentRef: ParentActor;
    savedAnswer?: SavedQuestionAnswer;
    hidden?: boolean;
    excluded: boolean;
    hiddenByAnswersHide?: boolean;
}

export interface QuestionAnswerInput extends Partial<QuestionAnswerModel> {
    questionId: number;
    questionType: QuestionType;
    id: number;
    parentRef: ParentActor;
    savedAnswer: SavedQuestionAnswer;
}

export enum AnswerState {
    ready = 'ready',
}

export type QuestionAnswerEvent =
    // | { type: 'CLEAR' }
    | ({ type: 'RESPONSE_CHANGE' } & ResponseEventParams)
    | ({ type: 'RESPONSE_CHANGE_COMMENT' } & ResponseEventParams)
    | ({ type: 'RESPONSE_CHANGE_EXTRA' } & ResponseEventParams)
    // | { type: 'CHANGE_COMMENT'; value: string }
    | { type: 'SET_ANSWERED_COUNT'; value: number }
    | { type: 'SET_VALID'; value: boolean }
    | { type: 'SET_CHECK_STATUS'; testCheckStatus: TestCheckStatus }
    | { type: 'SET_HIDDEN'; value: boolean }
    | { type: 'SET_QUOTA_HIDDEN'; value: boolean }
    | { type: 'UPDATE_TRANSLATIONS'; answer: QuestionAnswerEntity };

export const QuestionAnswerMachineDefinition = setup({
    types: {
        context: {} as QuestionAnswerContext,
        events: {} as QuestionAnswerEvent,
        input: {} as QuestionAnswerInput,
    },
}).createMachine({
    id: 'QuestionAnswerMachine',
    initial: AnswerState.ready,
    context: ({ input }) => ({ ...cloneDeep(initialQuestionAnswer), ...input }),
    entry: [
        assign({
            response: ({ context, spawn, self }) => {
                return spawn(QuestionResponseMachine, {
                    systemId: `question-${context.questionId}-answer-${context.id}-response`,
                    input: {
                        questionId: context.questionId,
                        answerId: context.id,
                        parentRef: self,
                        savedResponse: context.savedAnswer?.response,
                    },
                });
            },
        }),
    ],
    states: {
        [AnswerState.ready]: {
            on: {
                RESPONSE_CHANGE: {
                    actions: [
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ event }) => {
                                return { ...event, type: 'QUESTION_RESPONSE_CHANGE' };
                            },
                        ),
                    ],
                },
                RESPONSE_CHANGE_COMMENT: {
                    actions: [
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ event }) => {
                                return { ...event, type: 'QUESTION_RESPONSE_CHANGE_COMMENT' };
                            },
                        ),
                    ],
                },
                // CHANGE_COMMENT: {
                //     actions: [
                //         sendTo(
                //             ({ context }) => context.parentRef,
                //             ({ event, context }) => ({
                //                 ...event,
                //                 type: 'QUESTION_RESPONSE_CHANGE_COMMENT',
                //                 questionId: context.questionId
                //             })
                //         )
                //     ]
                // },
                RESPONSE_CHANGE_EXTRA: {
                    actions: [
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ event, context }) => ({
                                ...event,
                                type: 'QUESTION_RESPONSE_CHANGE_EXTRA',
                                questionId: context.questionId,
                            }),
                        ),
                    ],
                },
                SET_ANSWERED_COUNT: {
                    actions: assign(({ event: { value } }) => ({ answeredCount: value })),
                },
                SET_VALID: {
                    actions: assign(({ event: { value } }) => ({ valid: value })),
                },
                SET_CHECK_STATUS: {
                    actions: assign(({ event: { testCheckStatus } }) => ({ testCheckStatus })),
                },
                SET_HIDDEN: {
                    actions: assign(({ event: { value } }) => {
                        return { hidden: value };
                    }),
                },
                SET_QUOTA_HIDDEN: {
                    actions: assign(({ event: { value } }) => {
                        return { hidden: value, quotaHidden: value };
                    }),
                },
            },
        },
    },
    on: {
        UPDATE_TRANSLATIONS: {
            actions: assign(({ event: { answer } }) => {
                return {
                    value: answer.value,
                };
            }),
        },
    },
});

export type QuestionAnswerActor = ActorRefFrom<typeof QuestionAnswerMachineDefinition>;
